/**
 * API エラーを画面に出せる日本語文へ変換する (Issue #672)。
 *
 * oRPC は入力検証に失敗すると `message` が固定文言 `"Input validation failed"` の BAD_REQUEST を返し、
 * 項目別の理由は `data.issues` にだけ載る (`@orpc/server` の `validateInput`)。`err.message` をそのまま
 * 表示すると contract に書いた日本語メッセージが全部捨てられ、利用者はどの項目が悪いのか分からない。
 *
 * contract schema の `safeParse` が返す issue も同じ形なので、送信前のクライアント側検証と
 * サーバ由来の検証エラーを同じ経路で表示できる (規則を二重に書かないための前提)。
 */

/** 検証 issue 1 件。`field` は先頭のパス要素 (トップレベル項目名)、パス無しは null。 */
export interface FieldIssue {
  field: string | null;
  message: string;
}

/** 項目キー → 画面に出す日本語ラベル。未定義の項目はキー名をそのまま出す。 */
export type LabelMap = Readonly<Record<string, string | undefined>>;

/** issues を取り出せない検証エラーで出す代替文 (英語の固定文言を画面に出さないため)。 */
export const VALIDATION_FALLBACK_MESSAGE = "入力内容に誤りがあります。各項目をご確認ください";

/** oRPC が入力検証失敗時に付ける固定文言。 */
const ORPC_VALIDATION_MESSAGE = "Input validation failed";

/**
 * issue の path から項目名を取り出す。
 * Standard Schema の path は `["name"]` と `[{ key: "name" }]` の両形式がありうる。
 */
function firstPathSegment(path: unknown): string | null {
  if (!Array.isArray(path) || path.length === 0) return null;
  const head: unknown = path[0];
  if (typeof head === "string" || typeof head === "number") return String(head);
  if (typeof head === "object" && head !== null && "key" in head) {
    const key: unknown = (head as { key: unknown }).key;
    if (typeof key === "string" || typeof key === "number") return String(key);
  }
  return null;
}

/** Standard Schema / zod の issue 配列を {@link FieldIssue} へ正規化する。message 無しは捨てる。 */
export function toFieldIssues(issues: readonly unknown[]): FieldIssue[] {
  const result: FieldIssue[] = [];
  for (const issue of issues) {
    if (typeof issue !== "object" || issue === null) continue;
    const message: unknown = (issue as { message?: unknown }).message;
    if (typeof message !== "string" || message === "") continue;
    result.push({ field: firstPathSegment((issue as { path?: unknown }).path), message });
  }
  return result;
}

/**
 * 入力検証エラーなら issue 配列を、それ以外のエラーなら null を返す。
 *
 * `data.issues` を持たない (固定文言だけの) 検証エラーも検証エラーとして扱い、空配列を返す。
 * 呼出側は「検証エラーだが項目が分からない」ケースを代替文にフォールバックできる。
 */
export function validationIssuesOf(err: unknown): FieldIssue[] | null {
  if (typeof err !== "object" || err === null) return null;
  const data: unknown = (err as { data?: unknown }).data;
  const rawIssues: unknown =
    typeof data === "object" && data !== null ? (data as { issues?: unknown }).issues : undefined;
  if (Array.isArray(rawIssues)) return toFieldIssues(rawIssues);
  return (err as { message?: unknown }).message === ORPC_VALIDATION_MESSAGE ? [] : null;
}

/** 項目に紐付く issue を「ラベル: 理由」の 1 行にする。 */
function describeIssue(issue: FieldIssue, labels: LabelMap): string {
  if (issue.field === null) return issue.message;
  return `${labels[issue.field] ?? issue.field}: ${issue.message}`;
}

/**
 * エラー表示枠が 1 つしかない画面向けに、項目ラベル付きの日本語文 (改行区切り) を作る。
 * 検証エラー以外は元のメッセージをそのまま返す (通信断・500 の文言を変えない)。
 */
export function formatApiError(err: unknown, labels: LabelMap = {}): string {
  const issues = validationIssuesOf(err);
  if (issues === null) return err instanceof Error ? err.message : String(err);
  const lines = issues.map((issue) => describeIssue(issue, labels));
  return lines.length > 0 ? lines.join("\n") : VALIDATION_FALLBACK_MESSAGE;
}

/**
 * issue を「画面に出している項目のもの」と「それ以外」に振り分ける。
 *
 * `byField` は各入力欄の直下に、`general` はダイアログ上部・下部のエラー枠に出す。
 * 画面に無い項目や path 無しの issue を general に落とすことで、どの issue も黙って消えない。
 */
export function groupFieldIssues(
  issues: readonly FieldIssue[],
  renderedFields: readonly string[],
  labels: LabelMap = {},
): { byField: Record<string, string[]>; general: string[] } {
  const rendered = new Set(renderedFields);
  const byField: Record<string, string[]> = {};
  const general: string[] = [];
  for (const issue of issues) {
    if (issue.field !== null && rendered.has(issue.field)) {
      const messages = byField[issue.field] ?? [];
      messages.push(issue.message);
      byField[issue.field] = messages;
      continue;
    }
    general.push(describeIssue(issue, labels));
  }
  return { byField, general };
}
